import React, {
  Suspense,
  forwardRef,
  lazy,
  useMemo,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import type { AntdIconProps } from "@ant-design/icons/lib/components/AntdIcon";
import type { LucideProps } from "lucide-react";

export const ICON_SOURCES = {
  ANTD: "antd",
  LUCIDE: "lucide",
} as const;

export type IconSource = (typeof ICON_SOURCES)[keyof typeof ICON_SOURCES];

export const isIconSource = (value: unknown): value is IconSource =>
  value === ICON_SOURCES.ANTD || value === ICON_SOURCES.LUCIDE;

interface CommonIconProps {
  size?: number | string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLElement>;
  title?: string;
  loadingFallback?: React.ReactNode;
  "aria-label"?: string;
  "aria-hidden"?: boolean | "true" | "false";
}

type AntdSpecificProps = Omit<
  AntdIconProps,
  "ref" | "style" | "className" | "onClick"
>;

type LucideSpecificProps = Omit<
  LucideProps,
  "ref" | "size" | "color" | "style" | "className" | "onClick"
>;

type AntdIconComponentProps = CommonIconProps &
  AntdSpecificProps & {
    source: typeof ICON_SOURCES.ANTD;
    name: string;
  };

type LucideIconComponentProps = CommonIconProps &
  LucideSpecificProps & {
    source: typeof ICON_SOURCES.LUCIDE;
    name: string;
  };

export type IconProps = AntdIconComponentProps | LucideIconComponentProps;

const NullIcon: ComponentType = () => null;
NullIcon.displayName = "Icon.Missing";

const lucideFileName = (pascalName: string): string =>
  pascalName
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
    .replace(/([a-zA-Z])([0-9])/g, "$1-$2")
    .replace(/([0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z]{2,})/g, (m) => m.split("").join("-"))
    .toLowerCase();

const lazyComponentCache = new Map<
  string,
  LazyExoticComponent<ComponentType<any>>
>();

const warnMissing = (source: IconSource, name: string, err: unknown) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      `[Icon] Failed to dynamically load icon "${name}" from source "${source}".`,
      err,
    );
  }
};

const importLucideIcon = async (
  name: string,
): Promise<{ default: ComponentType<any> }> => {
  const fileName = lucideFileName(name);
  try {
    const mod = await import(
      /* webpackChunkName: "lucide-icon-[request]" */
      /* webpackInclude: /\.js$/ */
      `lucide-react/dist/esm/icons/${fileName}.js`
    );
    return { default: (mod.default ?? mod[name] ?? NullIcon) as ComponentType };
  } catch (err) {
    warnMissing(ICON_SOURCES.LUCIDE, name, err);
    return { default: NullIcon };
  }
};

const importAntdIcon = async (
  name: string,
): Promise<{ default: ComponentType<any> }> => {
  try {
    const mod = await import(
      /* webpackChunkName: "antd-icon-[request]" */
      /* webpackInclude: /\.js$/ */
      `@ant-design/icons/es/icons/${name}.js`
    );
    return { default: (mod.default ?? mod[name] ?? NullIcon) as ComponentType };
  } catch (err) {
    warnMissing(ICON_SOURCES.ANTD, name, err);
    return { default: NullIcon };
  }
};

const getLazyIcon = (source: IconSource, name: string) => {
  const key = `${source}:${name}`;
  const cached = lazyComponentCache.get(key);
  if (cached) return cached;

  const lazyComp = lazy(() =>
    source === ICON_SOURCES.LUCIDE
      ? importLucideIcon(name)
      : importAntdIcon(name),
  );

  lazyComponentCache.set(key, lazyComp);
  return lazyComp;
};

export const preloadIcon = (source: IconSource, name: string): void => {
  if (!name) return;
  const key = `${source}:${name}`;
  if (lazyComponentCache.has(key)) return;
  if (source === ICON_SOURCES.LUCIDE) {
    void importLucideIcon(name);
  } else {
    void importAntdIcon(name);
  }
  getLazyIcon(source, name);
};

const Icon = forwardRef<HTMLElement, IconProps>((props, ref) => {
  const {
    source,
    name,
    size,
    color,
    className,
    style,
    onClick,
    loadingFallback,
    ...rest
  } = props;

  const Component = useMemo(
    () => getLazyIcon(source, name),
    [source, name],
  );

  const rendered =
    source === ICON_SOURCES.ANTD ? (
      <Component
        ref={ref}
        className={className}
        style={{
          ...(size != null ? { fontSize: size } : null),
          ...(color != null ? { color } : null),
          ...style,
        }}
        onClick={onClick}
        {...(rest as AntdSpecificProps)}
      />
    ) : (
      <Component
        ref={ref}
        size={size}
        color={color}
        className={className}
        style={style}
        onClick={onClick}
        {...(rest as LucideSpecificProps)}
      />
    );

  return <Suspense fallback={loadingFallback ?? null}>{rendered}</Suspense>;
});

Icon.displayName = "Icon";

export default Icon;
